import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  ProposalBuilderData,
  ProposalBuilderVersionDoc,
  ProposalVersionSource,
  TimberNodeCostSnapshot,
} from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';

export function sumBillableDisplayFromProposal(data: ProposalBuilderData): number {
  const enabled = (data.nodeSets || []).filter(ns => ns.enabled !== false);
  const sum = enabled.reduce((s, ns) => s + (ns.billablePrices?.subtotal || 0), 0);
  const display = (data as ProposalBuilderData & { displayTotal?: number }).displayTotal;
  if (typeof display === 'number' && Number.isFinite(display)) {
    return Math.round(display);
  }
  return Math.round(sum);
}

/** Captures each structure’s timber materials total and billable timber kit at version time. */
export function extractTimberNodeSnapshots(data: ProposalBuilderData): TimberNodeCostSnapshot[] {
  const sets = data.nodeSets || [];
  return sets.map(ns => ({
    nodeSetId: ns.id,
    name: (ns.name || 'Structure').trim() || 'Structure',
    timberMaterialsTotal: Math.round(Number(ns.materials?.timberTotal) || 0),
    billableTimberKit:
      typeof ns.billablePrices?.timberKit === 'number' && Number.isFinite(ns.billablePrices.timberKit)
        ? Math.round(ns.billablePrices.timberKit)
        : undefined,
  }));
}

/** Display label for version history source badges. */
export function formatProposalVersionSource(source: ProposalVersionSource): string {
  switch (source) {
    case 'sent':
      return 'Sent';
    case 'ops_approved':
      return 'OPS approved';
    case 'rfq_staging':
      return 'Supplier RFQ';
    default:
      return 'Manual';
  }
}

export async function createProposalVersion(
  dealId: string,
  snapshot: ProposalBuilderData,
  meta: { label?: string; source: ProposalVersionSource; createdBy: string },
): Promise<string> {
  const billableTotalSnapshot = sumBillableDisplayFromProposal(snapshot);
  const timberNodeSnapshots = extractTimberNodeSnapshots(snapshot);
  const snapshotClean = removeUndefinedDeep({
    ...snapshot,
    id: dealId,
    dealId,
  }) as ProposalBuilderData;

  // Do not pass `createdAt: serverTimestamp()` through removeUndefinedDeep — it breaks FieldValue.
  const payload: Record<string, unknown> = {
    createdAt: serverTimestamp(),
    createdBy: meta.createdBy,
    source: meta.source,
    billableTotalSnapshot,
    timberNodeSnapshots,
    snapshot: snapshotClean,
  };
  if (meta.label !== undefined && meta.label !== '') {
    payload.label = meta.label;
  }

  const ref = await addDoc(collection(db, 'proposalBuilders', dealId, 'versions'), payload);
  return ref.id;
}

export async function listProposalVersions(
  dealId: string,
  maxVersions = 50
): Promise<ProposalBuilderVersionDoc[]> {
  const q = query(
    collection(db, 'proposalBuilders', dealId, 'versions'),
    orderBy('createdAt', 'desc'),
    limit(maxVersions)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<ProposalBuilderVersionDoc, 'id'>) }));
}

/** Display string for version list rows and snapshot subtitle. */
export function formatProposalVersionTimestamp(v: ProposalBuilderVersionDoc): string {
  const c = v.createdAt as unknown;
  let d: Date | null = null;
  if (c != null && typeof (c as { toDate?: () => Date }).toDate === 'function') {
    d = (c as { toDate: () => Date }).toDate();
  } else if (c instanceof Date) {
    d = c;
  } else if (c && typeof c === 'object' && 'seconds' in (c as object)) {
    const sec = (c as { seconds: number }).seconds;
    d = new Date(sec * 1000);
  }
  if (!d || Number.isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export async function getProposalVersion(
  dealId: string,
  versionId: string
): Promise<ProposalBuilderVersionDoc | null> {
  const ref = doc(db, 'proposalBuilders', dealId, 'versions', versionId);
  const s = await getDoc(ref);
  if (!s.exists()) return null;
  return { id: s.id, ...(s.data() as Omit<ProposalBuilderVersionDoc, 'id'>) };
}
